import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import type { ComponentProps, ComponentType, MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { LoadingContainer } from './styles'

type ButtonProps = ComponentProps<typeof Button>

type Props<T extends ButtonProps> = {
	// Enforce the loading state of the button from the caller.
	isLoading?: boolean
	onClick: (e: MouseEvent<HTMLButtonElement>) => Promise<unknown>
	onIsLoadingUpdated?: (isLoading: boolean) => unknown
} & (
	| (Omit<ButtonProps, 'onClick'> & { component?: never })
	| (Omit<T, 'onClick'> & { component: ComponentType<T> })
)

// A button wrapped to make asynchronous call on click and show a loading indicator in the meantime.
const AsyncButton = <T extends ButtonProps>(props: Props<T>) => {
	const {
		component: ButtonComponent = Button,
		children,
		disabled,
		isLoading: forceIsLoading,
		onClick,
		onIsLoadingUpdated,
		style,
		...otherProps
	} = props
	const [isLoading, setIsLoading] = useState(!!forceIsLoading)
	const finalIsLoading = forceIsLoading ?? isLoading
	const isMounted = useRef(false)
	useEffect(() => {
		isMounted.current = true
		return () => {
			isMounted.current = false
		}
	}, [])
	const handleClick = useCallback(
		async (e: MouseEvent<HTMLButtonElement>) => {
			setIsLoading(true)
			onIsLoadingUpdated?.(true)
			try {
				await onClick(e)
			} finally {
				if (isMounted.current) {
					setIsLoading(false)
					onIsLoadingUpdated?.(false)
				}
			}
		},
		[onClick, onIsLoadingUpdated]
	)
	const fullStyle = useMemo(
		() => ({
			position: 'relative',
			...style,
		}),
		[style]
	)
	return (
		<ButtonComponent
			{...(otherProps as unknown as T)}
			disabled={disabled || finalIsLoading}
			onClick={handleClick}
			style={fullStyle}
		>
			{children}
			{finalIsLoading ? (
				<LoadingContainer>
					<CircularProgress />
				</LoadingContainer>
			) : null}
		</ButtonComponent>
	)
}

export { AsyncButton }
